import { ORPCError } from "@orpc/client";
import { isAxiosError } from "axios";

/** A failed query is retried at most this many times, so it makes at most three calls. */
export const maxQueryRetries = 2;

/** Don't hold a page for a Retry-After longer than this; the query fails instead. */
export const maxRetryAfterMs = 30 * 1000;

const maxBackoffMs = 30 * 1000;
const tooManyRequests = 429;
const serviceUnavailable = 503;
const transientServerStatuses = new Set([502, serviceUnavailable, 504]);

export type QueryRetryReason = "timeout" | "network" | "server" | "throttled";

const isRecord = (value: unknown): value is Record<string, unknown> => typeof value === "object" && value !== null;

const isORPCTimeout = (error: unknown): error is ORPCError<string, unknown> => error instanceof ORPCError && error.code === "AbortError";

export const isTimeoutError = (error: unknown) => isORPCTimeout(error) || (isAxiosError(error) && error.code === "ECONNABORTED");

/** fetch rejects with a TypeError when the network fails (Chromium: "Failed to fetch", Firefox: "NetworkError…", Safari: "Load failed"). */
export const isNetworkError = (error: unknown) =>
    (error instanceof TypeError && /failed to fetch|networkerror|load failed/i.test(error.message)) || (isAxiosError(error) && error.code === "ERR_NETWORK");

const getResponse = (error: unknown): { status?: number; headers?: unknown } | undefined => {
    if (error instanceof ORPCError) {
        const data: unknown = error.data;
        return { status: error.status, headers: isRecord(data) ? data.headers : undefined };
    }
    if (isAxiosError(error) && error.response) {
        return { status: error.response.status, headers: error.response.headers };
    }
    return undefined;
};

const parseRetryAfter = (value: unknown, now: number): number | undefined => {
    const header: unknown = Array.isArray(value) ? value[0] : value;
    if (typeof header !== "string" || !header.trim()) {
        return undefined;
    }
    const seconds = Number(header.trim());
    if (Number.isFinite(seconds)) {
        return Math.max(0, seconds * 1000);
    }
    const date = Date.parse(header);
    return Number.isNaN(date) ? undefined : Math.max(0, date - now);
};

/** The wait a 429 or 503 response asks for in its Retry-After header (seconds or an HTTP date), in milliseconds. */
export const getRetryAfterMs = (error: unknown, now = Date.now()): number | undefined => {
    const response = getResponse(error);
    if (response?.status !== tooManyRequests && response?.status !== serviceUnavailable) {
        return undefined;
    }
    return isRecord(response.headers) ? parseRetryAfter(response.headers["retry-after"], now) : undefined;
};

/**
 * Why a failed query is worth retrying, or undefined if it isn't:
 * - timeout: only requests that used the default timeout. Endpoints with their own timeout (timeout.ts or a per-call value)
 * already wait as long as their server can answer, so another call would just wait again.
 * - network: fetch failed before a response arrived.
 * - throttled: 429 or 503 with a Retry-After of at most maxRetryAfterMs (the rules of oRPC's RetryAfterPlugin).
 * - server: 502, 503 or 504 without a Retry-After.
 */
export const getQueryRetryReason = (error: unknown, now = Date.now()): QueryRetryReason | undefined => {
    if (isTimeoutError(error)) {
        const data: unknown = isORPCTimeout(error) ? error.data : undefined;
        return isRecord(data) && data.isDefaultTimeout === false ? undefined : "timeout";
    }
    if (isNetworkError(error)) {
        return "network";
    }
    const status = getResponse(error)?.status;
    if (status === tooManyRequests || status === serviceUnavailable) {
        const retryAfterMs = getRetryAfterMs(error, now);
        if (retryAfterMs !== undefined) {
            return retryAfterMs <= maxRetryAfterMs ? "throttled" : undefined;
        }
    }
    return status !== undefined && transientServerStatuses.has(status) ? "server" : undefined;
};

export const shouldRetryQuery = (failureCount: number, error: unknown) => failureCount < maxQueryRetries && getQueryRetryReason(error) !== undefined;

/** Waits as long as Retry-After asks; otherwise TanStack's default backoff (1 s, 2 s, 4 s, … up to 30 s). */
export const getQueryRetryDelay = (failureCount: number, error: unknown) => getRetryAfterMs(error) ?? Math.min(1000 * 2 ** failureCount, maxBackoffMs);
